import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminChangePassword } from '@/lib/api';

export default function ChangePasswordModal({ open, onOpenChange, onChanged }) {
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrentPwd('');
    setNewPwd('');
    setConfirmPwd('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('New passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await adminChangePassword({ current_password: currentPwd, new_password: newPwd });
      toast.success('Password updated. Please sign in again.');
      reset();
      onOpenChange(false);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle>Change admin password</DialogTitle>
          <DialogDescription>
            You&apos;ll be signed out after changing the password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
          <div>
            <Label htmlFor="cur-pwd" className="mb-1.5 block text-sm font-medium">
              Current password
            </Label>
            <Input
              id="cur-pwd"
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              className="h-11 rounded-xl"
              data-testid="current-password-input"
              required
            />
          </div>
          <div>
            <Label htmlFor="new-pwd" className="mb-1.5 block text-sm font-medium">
              New password
            </Label>
            <Input
              id="new-pwd"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              className="h-11 rounded-xl"
              data-testid="new-password-input"
              required
              minLength={8}
            />
          </div>
          <div>
            <Label htmlFor="confirm-pwd" className="mb-1.5 block text-sm font-medium">
              Confirm new password
            </Label>
            <Input
              id="confirm-pwd"
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              className="h-11 rounded-xl"
              data-testid="confirm-password-input"
              required
              minLength={8}
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !currentPwd || !newPwd || !confirmPwd}
              className="hover:bg-[hsl(var(--brand-blue-2))]"
              data-testid="change-password-submit-button"
            >
              {busy ? 'Updating…' : 'Change password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
